'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '@/components/Button'
import { Bell, X } from 'lucide-react'
import { usePushSubscription } from '@/hooks/usePushSubscription'
import { useUserContext } from '@/providers/UserProvider'

const PROMPT_DISMISSED_KEY = 'push-subscription-prompt-dismissed'
const PROMPT_SHOWN_COUNT_KEY = 'push-subscription-prompt-shown-count'
const MAX_SHOW_COUNT = 3 // Show prompt maximum 3 times

/**
 * Onboarding prompt that encourages users to enable push notifications.
 * Shows once on first login or dashboard visit, with ability to dismiss.
 * Will show maximum 3 times if dismissed, then permanently hidden.
 */
export function PushSubscriptionPrompt() {
  const { user } = useUserContext()
  const {
    isSubscribed,
    isLoading,
    isSupported,
    permission,
    subscribe,
  } = usePushSubscription()

  const [isVisible, setIsVisible] = useState(false)
  const [isSubscribing, setIsSubscribing] = useState(false)

  useEffect(() => {
    // Don't show if not supported, no user, or already subscribed
    if (!isSupported || !user || isSubscribed) {
      return
    }

    // Don't show if permission already denied
    if (permission === 'denied') {
      return
    }

    // Check if prompt was dismissed
    const dismissed = localStorage.getItem(PROMPT_DISMISSED_KEY)
    if (dismissed === 'true') {
      return
    }

    // Check show count
    const shownCountStr = localStorage.getItem(PROMPT_SHOWN_COUNT_KEY)
    const shownCount = shownCountStr ? parseInt(shownCountStr, 10) : 0

    if (shownCount >= MAX_SHOW_COUNT) {
      // Permanently dismissed after max shows
      localStorage.setItem(PROMPT_DISMISSED_KEY, 'true')
      return
    }

    // Show prompt after a short delay for better UX
    const timer = setTimeout(() => {
      setIsVisible(true)
      // Increment show count
      localStorage.setItem(PROMPT_SHOWN_COUNT_KEY, String(shownCount + 1))
    }, 2000)

    return () => clearTimeout(timer)
  }, [isSupported, user, isSubscribed, permission])

  const handleSubscribe = async () => {
    setIsSubscribing(true)
    const success = await subscribe()
    if (success) {
      setIsVisible(false)
      localStorage.setItem(PROMPT_DISMISSED_KEY, 'true')
    }
    setIsSubscribing(false)
  }

  const handleDismiss = () => {
    setIsVisible(false)
    // Don't permanently dismiss on "Later" click, allow showing again next time
  }

  const handlePermanentDismiss = () => {
    setIsVisible(false)
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true')
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-slide-up">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-5 relative">
        <button
          onClick={handlePermanentDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Permanent sluiten"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">
              Mis geen update meer!
            </h3>
            <p className="text-sm text-gray-600">
              Schakel push notificaties in en ontvang direct berichten over vermeldingen,
              samenwerkingen en moderatie updates.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSubscribe}
            size="sm"
            className="flex-1"
            disabled={isLoading || isSubscribing}
          >
            {isSubscribing ? 'Bezig...' : 'Inschakelen'}
          </Button>
          <Button
            onClick={handleDismiss}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            Later
          </Button>
        </div>

        <p className="text-xs text-gray-500 mt-2 text-center">
          Je kunt dit altijd later instellen via je profiel
        </p>
      </div>
    </div>
  )
}
